import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNotificationTemplateVariables, useNotificationTemplates } from "services/hooks/rentals";
import { CHANNEL_OPTIONS, isScheduledTemplate } from "../constants";

const getTemplatePayload = (form) => ({
  related_organization: form.related_organization ? Number(form.related_organization) : null,
  name: form.name.trim(),
  channel: form.channel,
  subject: form.subject ?? "",
  body: form.body,
  is_active: form.is_active,
});

const TemplatesTab = () => {
  const {
    templates,
    isLoadingTemplates,
    errorTemplates,
    isSubmittingTemplate,
    templateActionError,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useNotificationTemplates();
  const scheduledTemplates = useMemo(() => templates.filter(isScheduledTemplate), [templates]);
  const {
    templateVariables,
    templateVariableGroups,
    isLoadingTemplateVariables,
    templateVariablesError,
  } = useNotificationTemplateVariables();
  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const bodyInputRef = useRef(null);
  const [form, setForm] = useState({
    related_organization: "",
    name: "",
    channel: "EMAIL",
    subject: "",
    body: "",
    is_active: true,
  });

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({
      related_organization: "",
      name: "",
      channel: "EMAIL",
      subject: "",
      body: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    setForm({
      related_organization: row.related_organization ?? "",
      name: row.name || "",
      channel: row.channel || "EMAIL",
      subject: row.subject || "",
      body: row.body || "",
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      setMessage({ type: "error", text: "Template name and body are required." });
      return;
    }
    const payload = getTemplatePayload(form);
    const response = editingId
      ? await updateTemplate(editingId, payload)
      : await createTemplate(payload);
    if (response.success) {
      setMessage({
        type: "success",
        text: editingId ? "Template updated successfully." : "Template created successfully.",
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || "Failed to save template." });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    const response = await deleteTemplate(id);
    if (response.success) {
      setMessage({ type: "success", text: "Template deleted." });
    } else {
      setMessage({ type: "error", text: response.message || "Failed to delete template." });
    }
  };

  const insertTokenInBody = (token) => {
    const input = bodyInputRef.current;
    const currentBody = form.body || "";

    if (!input || typeof input.selectionStart !== "number" || typeof input.selectionEnd !== "number") {
      setForm((prev) => ({ ...prev, body: `${prev.body || ""}${token}` }));
      return;
    }

    const start = input.selectionStart;
    const end = input.selectionEnd;
    const nextBody = `${currentBody.slice(0, start)}${token}${currentBody.slice(end)}`;
    const nextCursor = start + token.length;

    setForm((prev) => ({ ...prev, body: nextBody }));

    setTimeout(() => {
      if (bodyInputRef.current) {
        bodyInputRef.current.focus();
        bodyInputRef.current.setSelectionRange(nextCursor, nextCursor);
      }
    }, 0);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Scheduled Templates</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Add Template
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        For cron-based tenant due and landlord payout rules only — no <code>event_key</code>. Instant and broadcast
        templates are under Event Templates.
      </Typography>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {errorTemplates && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load templates.
        </Alert>
      )}

      {templateActionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {templateActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingTemplates ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduledTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No scheduled templates found.</TableCell>
                </TableRow>
              ) : (
                scheduledTemplates.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell>{row.related_organization ?? "Global"}</TableCell>
                    <TableCell>{row.channel || "—"}</TableCell>
                    <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Edit Template" : "Create Template"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Organisation ID (optional for global)"
            value={form.related_organization}
            onChange={(event) => setForm((prev) => ({ ...prev, related_organization: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Template Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Channel</InputLabel>
            <Select
              label="Channel"
              value={form.channel}
              onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))}
            >
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Subject"
            value={form.subject}
            onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            minRows={4}
            multiline
            label="Body"
            value={form.body}
            inputRef={bodyInputRef}
            onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
          />

          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Placeholder Token Picker
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Click a token to insert it in the template body.
            </Typography>

            {isLoadingTemplateVariables ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading template variables...</Typography>
              </Box>
            ) : Object.keys(templateVariableGroups).length > 0 ? (
              <Stack spacing={2}>
                {Object.entries(templateVariableGroups).map(([groupKey, variables], index) => (
                  <Box key={groupKey}>
                    {index > 0 && <Divider sx={{ mb: 2 }} />}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      {groupKey === "tenant_due" ? "Tenant due variables" : groupKey === "landlord_payout" ? "Landlord payout variables" : groupKey}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {variables.map((variable) => (
                        <Chip
                          key={variable.key}
                          label={variable.token}
                          title={variable.description || variable.key}
                          onClick={() => insertTokenInBody(variable.token)}
                          variant="outlined"
                          clickable
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Tenant Due Variables
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {(templateVariables.tenant_due || []).map((variable) => (
                      <Chip
                        key={variable.key}
                        label={variable.token}
                        title={variable.description || variable.key}
                        onClick={() => insertTokenInBody(variable.token)}
                        variant="outlined"
                        clickable
                      />
                    ))}
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Landlord Payout Variables
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {(templateVariables.landlord_payout || []).map((variable) => (
                      <Chip
                        key={variable.key}
                        label={variable.token}
                        title={variable.description || variable.key}
                        onClick={() => insertTokenInBody(variable.token)}
                        variant="outlined"
                        clickable
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>
            )}

            {templateVariablesError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {templateVariablesError}
              </Alert>
            )}
          </Box>

          <Box display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
            <Typography variant="body2">Active</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSubmittingTemplate}>
            {isSubmittingTemplate ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesTab;
